/**
 * Shared book-cover color palette — used by every surface that renders a
 * book card (Emporium main grid, Recently Added rail, Library grid,
 * Continue Listening rail). Single source of truth so a romance novel
 * looks the same mauve everywhere it appears.
 *
 * `getBookColor` returns the base background, `getAccentColor` returns the
 * trim/border color, and `getBookCoverGradient` returns the ready-to-use
 * CSS background string so callers don't have to repeat the gradient math.
 */

const COLOR_MAP = {
  // Romance — Mauve-pink
  'Romance': '#D8A7B1',
  'Gothic Romance': '#4B0082',

  // Children's Literature — Whimsical yellow
  "Children's Literature": '#FFF9C4',
  'Fantasy': '#228B22',

  // Literary Fiction — Warm taupe
  'Literary Fiction': '#6B5B4C',

  // Horror & Dark Themes
  'Gothic Horror': '#4A0E0E',
  'Gothic Fiction': '#2A0E0E',

  // Science & Mystery
  'Science Fiction': '#2F4F4F',
  'Mystery': '#2F4F4F',
  'Philosophical Fiction': '#483D8B',

  // Adventure & Historical
  'Adventure': '#191970',
  'Historical Fiction': '#704214',
  'War Fiction': '#3E4A3B',

  // Special Categories
  'Christmas Fiction': '#006400',
  'Poetry': '#6A5ACD',
  'Humor': '#FF6B6B',
  'Satire': '#9B59B6',
  'Epic Poetry': '#8B7355',
  'Classic': '#6B5B4C',

  // Fallback by title keyword
  'pride': '#D8A7B1',
  'dracula': '#4A0E0E',
  'frankenstein': '#2F4F4F',
  'wizard': '#228B22',
  'alice': '#FFF9C4',
  'peter pan': '#FFF9C4',
  'dorian': '#483D8B',
  'sherlock': '#2F4F4F',
  'wuthering': '#4B0082',
  'jane eyre': '#D8A7B1',
  'monte cristo': '#191970',
  'christmas': '#006400',
};

const ACCENT_MAP = {
  'Romance': '#8B4576',
  'Gothic Romance': '#8B4789',

  "Children's Literature": '#64B5F6',
  'Fantasy': '#FFD700',

  'Literary Fiction': '#B8906C',

  'Gothic Horror': '#DC143C',
  'Gothic Fiction': '#DC143C',

  'Science Fiction': '#4682B4',
  'Mystery': '#B8860B',
  'Philosophical Fiction': '#9370DB',

  'Adventure': '#FFD700',
  'Historical Fiction': '#C19A6B',
  'War Fiction': '#8B7D6B',

  'Christmas Fiction': '#DC143C',
  'Poetry': '#E6E6FA',
  'Humor': '#FFD93D',
  'Satire': '#F39C12',
  'Epic Poetry': '#D4AF37',
  'Classic': '#B8906C',

  'pride': '#8B4576',
  'dracula': '#DC143C',
  'frankenstein': '#4682B4',
  'wizard': '#FFD700',
  'alice': '#64B5F6',
  'peter pan': '#64B5F6',
  'dorian': '#9370DB',
  'sherlock': '#B8860B',
  'wuthering': '#8B4789',
  'jane eyre': '#8B4576',
  'monte cristo': '#FFD700',
  'christmas': '#DC143C',
};

const DEFAULT_COLOR = '#2C3A4C';
const DEFAULT_ACCENT = '#D7B377';

const lookup = (map, book, fallback) => {
  if (!book) return fallback;
  if (book.genre && map[book.genre]) return map[book.genre];
  const title = (book.title || '').toLowerCase();
  if (title) {
    for (const keyword in map) {
      if (title.includes(keyword.toLowerCase())) return map[keyword];
    }
  }
  return fallback;
};

export const getBookColor = (book) => {
  if (book && book.color) return book.color;
  return lookup(COLOR_MAP, book, DEFAULT_COLOR);
};

export const getAccentColor = (book) => {
  if (book && book.accentColor) return book.accentColor;
  return lookup(ACCENT_MAP, book, DEFAULT_ACCENT);
};

/**
 * Children's Literature gets a whimsical pastel gradient so the cover reads
 * as warm/sunny instead of dark. All other genres use the standard
 * base→accent gradient that mirrors the full-size BookCard.
 */
export const getBookCoverGradient = (book) => {
  const isChildrens = book && book.genre === "Children's Literature";
  const base = getBookColor(book);
  const accent = getAccentColor(book);
  if (isChildrens) {
    return 'linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 50%, #FFF59D 100%)';
  }
  return `linear-gradient(135deg, ${base} 0%, ${accent}40 100%)`;
};

export const getBookTextColor = (book) =>
  (book && book.genre === "Children's Literature") ? '#2C3A4C' : '#F7F3E9';
